import {
  DocumentSmartTextCollection,
  DocumentSmartTextField,
  DocumentSmartTextOrder,
  DocumentSmartTextReferenceCounts
} from "./types";
import { SMART_FIELD_REGEX } from "lib/constants";

type DesignLike = {
  documentSmartText: DocumentSmartTextCollection;
  documentSmartTextOrder?: DocumentSmartTextOrder;
  elements: Record<string, AnyElement>;
  pages: Record<string, PageLike>;
  version: number;
  // The host Design class (Design.js) is untyped, so the typed constructor
  // shape is impossible to honour from a class declaration. Loose `any`
  // here matches both production Design and TS test doubles.

  constructor: any;
};

type PageLike = {
  elementsOrder?: string[];
};

type AnyElement = {
  uniqueId: string;
  type: string;
  value?: string;
  displayValue?: string;
  elementsOrder?: string[];
  cells?: Record<string, { uniqueId: string; value?: string }>;
};

const DOC_PREFIX = "doc.";

/**
 * Returns the list of document.* field ids referenced in a textbox value.
 */
export const getDocumentSmartTextRefsFromValue = (
  value?: string | null
): string[] => {
  if (!value) return [];
  const refs: string[] = [];
  const regex = new RegExp(SMART_FIELD_REGEX.source, "g");
  let match: RegExpExecArray | null;
  while ((match = regex.exec(value)) !== null) {
    const captured = match[1];
    if (!captured) continue;
    const key = captured.trim();
    if (key.startsWith(DOC_PREFIX)) {
      refs.push(key.slice(DOC_PREFIX.length));
    }
  }
  return refs;
};

/**
 * Strip a single {doc.<id>} token from a string value.
 * Multiple occurrences and surrounding whitespace are handled.
 */
export const stripDocumentTokenFromValue = (
  value: string | undefined,
  id: string
): string => {
  if (!value) return value || "";
  const tokenRegex = new RegExp(`\\{\\s*doc\\.${escapeRegex(id)}\\s*\\}`, "g");
  return value.replace(tokenRegex, "");
};

const hasOwn = (collection: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(collection, key);

/**
 * Appends any ids missing from `order`, preserving the existing order and
 * skipping ids with no backing field. The single reconciliation rule used
 * wherever a documentSmartTextOrder is rebuilt.
 */
export const appendMissingIdsToOrder = (
  order: DocumentSmartTextOrder,
  ids: string[],
  collection?: DocumentSmartTextCollection
): DocumentSmartTextOrder => {
  const next = [...order];
  ids.forEach(id => {
    if (collection && !hasOwn(collection, id)) return;
    if (!next.includes(id)) next.push(id);
  });
  return next;
};

/**
 * Re-key a collection by each entry's own `.id`.
 *
 * Design payloads are camelized on the way in (humps.camelizeKeys at the api
 * boundary), which lowercases the first character of every object key. That
 * rewrites documentSmartText map keys while leaving the nested `.id` and the
 * in-text `{doc.<id>}` tokens untouched, so a field id like `StoreLocation`
 * lands under key `storeLocation` and stops matching its own references.
 *
 * Limitation: this runs *after* camelizeKeys, so it can only repair a single
 * mangled entry. A design that already persisted a buggy duplicate pair
 * (e.g. { item1, Item1 }) has had both keys collapsed to one by camelizeKeys
 * before we see it, so the value-bearing entry may already be lost. Fully
 * recovering those requires not mangling the keys in the first place (skip the
 * documentSmartText subtree at the camelize boundary) — see follow-up.
 */
export const rekeyDocumentSmartTextById = (
  collection?: DocumentSmartTextCollection | null
): DocumentSmartTextCollection => {
  const out: DocumentSmartTextCollection = {};
  const source = collection || {};
  Object.keys(source).forEach(key => {
    const entry = source[key];
    if (!entry) return;
    const id = typeof entry.id === "string" && entry.id ? entry.id : key;
    out[id] = entry;
  });
  return out;
};

type ImportedPagePayload = {
  data?: {
    documentSmartText?: DocumentSmartTextCollection;
    documentSmartTextOrder?: DocumentSmartTextOrder;
  };
};

export type ImportedDocumentSmartText = {
  fields: DocumentSmartTextCollection;
  order: DocumentSmartTextOrder;
};

/**
 * Gathers the document Smart Data carried by imported page payloads (a layout
 * added as a new page). documentSmartText is stamped design-wide onto every
 * page on save, so each payload carries the source design's whole collection.
 * When several imported pages disagree on a field id, the first wins.
 */
export const collectImportedDocumentSmartText = (
  pages: ImportedPagePayload[]
): ImportedDocumentSmartText =>
  (pages || []).reduce<ImportedDocumentSmartText>(
    (acc, pageData) => {
      const page = pageData?.data || {};
      // Re-key first: the imported payload is camelized like any other
      // fetched design, so its map keys can disagree with the field ids the
      // `{doc.<id>}` tokens reference.
      const pageFields = rekeyDocumentSmartTextById(page.documentSmartText);
      const pageOrder = page.documentSmartTextOrder || [];

      Object.keys(pageFields).forEach(fieldId => {
        const field = pageFields[fieldId];
        // hasOwnProperty, not truthiness: field ids come from user-selected
        // text (see idGeneration), so an id like "constructor" would read
        // truthy off Object.prototype and silently drop the field.
        if (field && !hasOwn(acc.fields, fieldId)) {
          acc.fields[fieldId] = field;
        }
      });

      acc.order = appendMissingIdsToOrder(acc.order, [
        ...pageOrder,
        ...Object.keys(pageFields)
      ]);

      return acc;
    },
    { fields: {}, order: [] }
  );

/**
 * Merges imported document Smart Data into a design. The design's own fields
 * win on id collision, so importing a layout never overwrites Smart Data the
 * user has already filled in; imported ids are appended to the order.
 */
export const mergeImportedDocumentSmartText = (
  design: {
    documentSmartText?: DocumentSmartTextCollection;
    documentSmartTextOrder?: DocumentSmartTextOrder;
  },
  imported: ImportedDocumentSmartText
): {
  documentSmartText: DocumentSmartTextCollection;
  documentSmartTextOrder: DocumentSmartTextOrder;
} => {
  const documentSmartText = {
    ...imported.fields,
    ...design.documentSmartText
  };

  return {
    documentSmartText,
    documentSmartTextOrder: appendMissingIdsToOrder(
      design.documentSmartTextOrder || [],
      [...imported.order, ...Object.keys(documentSmartText)],
      documentSmartText
    )
  };
};

const escapeRegex = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const DocumentSmartTextOps = {
  addDocumentSmartTextField(
    this: DesignLike,
    id: string,
    field: Partial<DocumentSmartTextField>
  ): unknown {
    // Bail on falsy or already-registered ids. Lets call sites (auto-discovery,
    // create flow, convert bar) drop their pre-checks and avoids silent
    // overwrites on duplicate auto-discovered tokens within a single keystroke
    // window.
    if (!id) return this;
    const existing = this.documentSmartText || {};
    if (existing[id]) return this;
    const type = field.type ?? "text";
    const hasTimeFormat = type === "time" || type === "datetime";
    const next: DocumentSmartTextField = {
      id,
      label: field.label ?? id,
      type,
      defaultValue: field.defaultValue ?? "",
      value: field.value ?? null,
      helper: field.helper,
      required: field.required ?? false,
      options: field.options,
      format:
        field.format ??
        (type === "date" || type === "datetime" ? "DD/MM/YYYY" : undefined),
      timeFormat: hasTimeFormat ? (field.timeFormat ?? "24hr") : undefined,
      showAmPm: hasTimeFormat ? (field.showAmPm ?? false) : undefined,
      requireDecimals: type === "number" ? field.requireDecimals : undefined,
      createdAt: field.createdAt ?? Date.now()
    };

    const existingOrder = this.documentSmartTextOrder || [];
    const nextOrder = existingOrder.includes(id)
      ? existingOrder
      : [...existingOrder, id];

    return new this.constructor({
      ...(this as unknown as object),
      documentSmartText: {
        ...existing,
        [id]: next
      },
      documentSmartTextOrder: nextOrder,
      version: this.version + 1
    });
  },

  updateDocumentSmartTextField(
    this: DesignLike,
    id: string,
    partialField: Partial<DocumentSmartTextField>
  ): unknown {
    const existing = this.documentSmartText || {};
    const current = existing[id];
    if (!current) return this;

    return new this.constructor({
      ...(this as unknown as object),
      documentSmartText: {
        ...existing,
        [id]: {
          ...current,
          ...partialField
        }
      },
      version: this.version + 1
    });
  },

  setDocumentSmartTextValue(
    this: DesignLike,
    id: string,
    value: string
  ): unknown {
    const existing = this.documentSmartText || {};
    const current = existing[id];
    if (!current) return this;

    return new this.constructor({
      ...(this as unknown as object),
      documentSmartText: {
        ...existing,
        [id]: {
          ...current,
          value
        }
      },
      version: this.version + 1
    });
  },

  renameDocumentSmartTextField(
    this: DesignLike,
    oldId: string,
    newId: string
  ): unknown {
    const existing = this.documentSmartText || {};
    const current = existing[oldId];
    if (!current) return this;
    if (oldId === newId) return this;
    if (existing[newId]) return this;

    const renamed: DocumentSmartTextField = { ...current, id: newId };
    const oldTokenRegex = new RegExp(
      `\\{\\s*doc\\.${escapeRegex(oldId)}\\s*\\}`,
      "g"
    );
    const newToken = `{doc.${newId}}`;

    const applyRename = (str: string): string => {
      oldTokenRegex.lastIndex = 0;
      return str.replace(oldTokenRegex, newToken);
    };

    const nextElements: Record<string, AnyElement> = { ...this.elements };
    Object.keys(nextElements).forEach(elementId => {
      const element = nextElements[elementId];
      if (!element) return;

      let nextElement = element;

      if (typeof element.value === "string") {
        const renamedValue = applyRename(element.value);
        if (renamedValue !== element.value) {
          nextElement = { ...nextElement, value: renamedValue };
        }
      }

      if (element.cells) {
        let cellsChanged = false;
        const nextCells = { ...element.cells };
        Object.keys(element.cells).forEach(cellId => {
          const cell = element.cells![cellId];
          if (!cell || typeof cell.value !== "string") return;
          const renamedValue = applyRename(cell.value);
          if (renamedValue === cell.value) return;
          nextCells[cellId] = { ...cell, value: renamedValue };
          cellsChanged = true;
        });
        if (cellsChanged) nextElement = { ...nextElement, cells: nextCells };
      }

      if (nextElement !== element) nextElements[elementId] = nextElement;
    });

    const { [oldId]: _omitted, ...remaining } = existing;
    void _omitted;

    const existingOrder = this.documentSmartTextOrder || [];
    const idx = existingOrder.indexOf(oldId);
    const nextOrder =
      idx === -1
        ? existingOrder.includes(newId)
          ? existingOrder
          : [...existingOrder, newId]
        : existingOrder.map(entry => (entry === oldId ? newId : entry));

    return new this.constructor({
      ...(this as unknown as object),
      elements: nextElements,
      documentSmartText: {
        ...remaining,
        [newId]: renamed
      },
      documentSmartTextOrder: nextOrder,
      version: this.version + 1
    });
  },

  removeDocumentSmartTextField(this: DesignLike, id: string): unknown {
    const existing = this.documentSmartText || {};
    if (!existing[id]) return this;

    // sweep every textbox value (and table2 cell) to strip the token
    const nextElements: Record<string, AnyElement> = { ...this.elements };
    Object.keys(nextElements).forEach(elementId => {
      const element = nextElements[elementId];
      if (!element) return;

      let nextElement = element;

      if (typeof element.value === "string") {
        const stripped = stripDocumentTokenFromValue(element.value, id);
        if (stripped !== element.value) {
          nextElement = { ...nextElement, value: stripped };
        }
      }

      if (element.cells) {
        let cellsChanged = false;
        const nextCells = { ...element.cells };
        Object.keys(element.cells).forEach(cellId => {
          const cell = element.cells![cellId];
          if (!cell || typeof cell.value !== "string") return;
          const stripped = stripDocumentTokenFromValue(cell.value, id);
          if (stripped === cell.value) return;
          nextCells[cellId] = { ...cell, value: stripped };
          cellsChanged = true;
        });
        if (cellsChanged) nextElement = { ...nextElement, cells: nextCells };
      }

      if (nextElement !== element) nextElements[elementId] = nextElement;
    });

    const { [id]: _omitted, ...remaining } = existing;
    void _omitted;

    const existingOrder = this.documentSmartTextOrder || [];
    const nextOrder = existingOrder.filter(entry => entry !== id);

    return new this.constructor({
      ...(this as unknown as object),
      elements: nextElements,
      documentSmartText: remaining,
      documentSmartTextOrder: nextOrder,
      version: this.version + 1
    });
  },

  /**
   * Scans every textbox value across all pages and tallies which pages
   * + element ids reference each document.* field.
   */
  getDocumentSmartTextReferenceCounts(
    this: DesignLike
  ): DocumentSmartTextReferenceCounts {
    const counts: DocumentSmartTextReferenceCounts = {};
    const collection = this.documentSmartText || {};

    // No document fields — skip the full-design scan entirely.
    if (Object.keys(collection).length === 0) {
      return counts;
    }

    Object.keys(collection).forEach(id => {
      counts[id] = {
        pages: new Set<string>(),
        elementIds: new Set<string>(),
        count: 0
      };
    });

    const tally = (element: AnyElement, pageId: string): void => {
      if (!element) return;
      const sources: string[] = [];
      if (typeof element.value === "string") sources.push(element.value);
      if (element.cells) {
        Object.values(element.cells).forEach(cell => {
          if (cell && typeof cell.value === "string") sources.push(cell.value);
        });
      }
      sources.forEach(source => {
        getDocumentSmartTextRefsFromValue(source).forEach(id => {
          if (!counts[id]) {
            counts[id] = {
              pages: new Set<string>(),
              elementIds: new Set<string>(),
              count: 0
            };
          }
          counts[id].pages.add(pageId);
          counts[id].elementIds.add(element.uniqueId);
          counts[id].count += 1;
        });
      });
    };

    Object.keys(this.pages || {}).forEach(pageId => {
      const order = this.pages[pageId]?.elementsOrder || [];
      order.forEach(elementId => {
        const element = this.elements[elementId];
        if (!element) return;
        if (element.type === "group" && element.elementsOrder) {
          element.elementsOrder.forEach(childId => {
            const child = this.elements[childId];
            if (child) tally(child, pageId);
          });
        } else {
          tally(element, pageId);
        }
      });
    });

    return counts;
  },

  /**
   * Removes documentSmartText entries with zero references.
   * Returns this if no pruning needed (no version bump).
   */
  pruneUnreferencedDocumentSmartText(this: DesignLike): unknown {
    const collection = this.documentSmartText || {};
    const ids = Object.keys(collection);
    if (!ids.length) return this;

    const counts =
      DocumentSmartTextOps.getDocumentSmartTextReferenceCounts.call(
        this
      ) as DocumentSmartTextReferenceCounts;

    const remaining: DocumentSmartTextCollection = {};
    let didPrune = false;
    ids.forEach(id => {
      const info = counts[id];
      const entry = collection[id];
      if (info && info.count > 0 && entry) {
        remaining[id] = entry;
      } else {
        didPrune = true;
      }
    });

    if (!didPrune) return this;

    const existingOrder = this.documentSmartTextOrder || [];
    const nextOrder = existingOrder.filter(entry => remaining[entry]);

    return new this.constructor({
      ...(this as unknown as object),
      documentSmartText: remaining,
      documentSmartTextOrder: nextOrder,
      version: this.version + 1
    });
  },

  /**
   * Persist an explicit ordering of document smart text field ids.
   * Defensive: drops ids not in the collection, appends any collection ids
   * missing from the supplied order so no field is ever lost.
   */
  updateDocumentSmartTextOrder(
    this: DesignLike,
    order: DocumentSmartTextOrder
  ): unknown {
    const collection = this.documentSmartText || {};
    const seen = new Set<string>();
    const filtered: string[] = [];
    (order || []).forEach(id => {
      if (!collection[id]) return;
      if (seen.has(id)) return;
      seen.add(id);
      filtered.push(id);
    });
    Object.keys(collection).forEach(id => {
      if (!seen.has(id)) {
        seen.add(id);
        filtered.push(id);
      }
    });

    return new this.constructor({
      ...(this as unknown as object),
      documentSmartTextOrder: filtered,
      version: this.version + 1
    });
  },

  /**
   * Returns the field ids in their persisted order, merged with any
   * collection ids that aren't represented in the stored order (appended
   * to the end). Used by the sidebar to render rows back-compat for
   * designs created before documentSmartTextOrder was persisted.
   */
  getOrderedDocumentSmartTextFieldIds(this: DesignLike): string[] {
    const collection = this.documentSmartText || {};
    const stored = this.documentSmartTextOrder || [];
    const seen = new Set<string>();
    const result: string[] = [];
    stored.forEach(id => {
      if (!collection[id]) return;
      if (seen.has(id)) return;
      seen.add(id);
      result.push(id);
    });
    Object.keys(collection).forEach(id => {
      if (!seen.has(id)) {
        seen.add(id);
        result.push(id);
      }
    });
    return result;
  }
};

export default DocumentSmartTextOps;
